import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Alert, Nav, Tab } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { validateTab } from '../validators';
import CardRenderer from './CardRenderer';
import ErrorBoundary from './ErrorBoundary';
import './blocks/ToggleButton.css';

const TreeTabRenderer = lazy(() => import('./TreeTabRenderer'));

interface TabData {
  id?: string;
  name?: string;
  card_list?: any[];
  cards_layout?: {
    layout?: string;
    columns?: number;
  };
  treeTabs?: any[];
  tree_tabs?: any[];
}

interface Schema {
  cards_layout?: {
    layout?: string;
    columns?: number;
  };
}

interface TabRendererProps {
  tabs: TabData[];
  activeTab?: string;
  setActiveTab?: (tabId: string) => void;
  updatedTabs?: Set<string>;
  clearTabUpdateIndicator?: (tabId: string) => void;
  schema?: Schema;
}

// Local helper to switch between two tree tabs via a toggle
const TreeTabsSwitcher: React.FC<{ treeTabs: any[] }> = ({ treeTabs }) => {
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const leftLabel =
    treeTabs?.[0]?.toggle_button_name || treeTabs?.[0]?.name || 'Option 1';
  const rightLabel =
    treeTabs?.[1]?.toggle_button_name || treeTabs?.[1]?.name || 'Option 2';

  const handleToggle = () => setActiveIdx(prev => (prev === 0 ? 1 : 0));

  return (
    <>
      <div
        className="toggle-button-container"
        style={{ marginBottom: '0.75rem' }}
      >
        <div className="toggle-with-labels">
          <div className="toggle-labels-group">
            <div
              className={`toggle-external-label ${activeIdx === 0 ? 'active' : ''}`}
              onClick={() => setActiveIdx(0)}
              role="button"
              tabIndex={0}
            >
              <span>{leftLabel}</span>
            </div>
            <span className="toggle-separator">/</span>
            <div
              className={`toggle-external-label ${activeIdx === 1 ? 'active' : ''}`}
              onClick={() => setActiveIdx(1)}
              role="button"
              tabIndex={0}
            >
              <span>{rightLabel}</span>
            </div>
          </div>
          <div className="toggle-switch" onClick={handleToggle}>
            <div
              className={`toggle-circle ${activeIdx === 1 ? 'right' : 'left'}`}
            ></div>
          </div>
        </div>
      </div>

      <ErrorBoundary fallbackType="card" title="TreeTab Error">
        <TreeTabRenderer treeTab={treeTabs[activeIdx]} />
      </ErrorBoundary>
    </>
  );
};

const TabRenderer: React.FC<TabRendererProps> = ({
  tabs,
  activeTab,
  setActiveTab,
  updatedTabs,
  clearTabUpdateIndicator,
  schema,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from query params or default to first valid tab
  const getInitialTab = (): string | null => {
    const tabFromUrl = searchParams.get('tab');

    // Check if the tab from URL exists (including invalid tabs with generated IDs)
    if (tabFromUrl) {
      const tabExists = tabs?.some((tab, index) => {
        const tabId = tab?.id || `invalid-tab-${index}`;
        return tabId === tabFromUrl;
      });
      if (tabExists) {
        return tabFromUrl;
      }
    }

    // Default to first tab (valid or invalid doesn't matter, we handle both)
    if (tabs?.[0]) {
      return tabs[0]?.id || `invalid-tab-0`;
    }

    return null;
  };

  const [activeTabLocal, setActiveTabLocal] = useState<string | null>(
    getInitialTab()
  );

  // Update local state when query parameter changes
  useEffect(() => {
    if (!tabs || !tabs.length) {
      return;
    }
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTabLocal) {
      // Check if the tab exists (including invalid tabs)
      const tabExists = tabs?.some((tab, index) => {
        const tabId = tab?.id || `invalid-tab-${index}`;
        return tabId === tabFromUrl;
      });
      if (tabExists) {
        setActiveTabLocal(tabFromUrl);
      }
    }
  }, [searchParams, tabs, activeTabLocal]);

  // Notify parent component when active tab changes
  useEffect(() => {
    if (activeTabLocal) {
      if (setActiveTab) {
        setActiveTab(activeTabLocal);
      }
    }
  }, [activeTabLocal, setActiveTab]);

  if (!tabs || !tabs.length) {
    console.log('No tabs found or empty tabs array');
    return null;
  }

  // Handle tab selection and update query parameters
  const handleTabSelect = (tabId: string | null) => {
    setActiveTabLocal(tabId);

    // Clear the update indicator when tab is selected
    if (clearTabUpdateIndicator && tabId) {
      clearTabUpdateIndicator(tabId);
    }

    // Update query parameters
    const newSearchParams = new URLSearchParams(searchParams);
    if (tabId) {
      newSearchParams.set('tab', tabId);
    } else {
      newSearchParams.delete('tab');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <Tab.Container
      id="deal-room-tabs"
      activeKey={activeTabLocal}
      onSelect={handleTabSelect}
    >
      <Nav variant="tabs" className="mb-3">
        {tabs.map((tab, index) => {
          const validation = validateTab(tab);
          const tabId = tab?.id || `invalid-tab-${index}`;
          const tabName = tab?.name || `⚠️ Invalid Tab ${index + 1}`;

          return (
            <Nav.Item key={tabId}>
              <Nav.Link
                eventKey={tabId}
                className={`tab-link-with-indicator ${!validation.isValid ? 'text-danger' : ''}`}
              >
                {validation.isValid ? tabName : `⚠️ ${tabName}`}
                {updatedTabs && updatedTabs.has(tab?.id) && (
                  <span className="tab-update-indicator"></span>
                )}
              </Nav.Link>
            </Nav.Item>
          );
        })}
      </Nav>

      <Tab.Content>
        {tabs.map((tab, index) => {
          const validation = validateTab(tab);
          const tabId = tab?.id || `invalid-tab-${index}`;

          // Handle invalid tabs
          if (!validation.isValid) {
            return (
              <Tab.Pane key={tabId} eventKey={tabId}>
                <div className="tab-content-wrapper">
                  <Alert variant="danger">
                    <Alert.Heading>⚠️ Invalid Tab Data</Alert.Heading>
                    <p className="mb-2 text-muted small">
                      <strong>ept AI backend error.</strong> Please send an
                      email to{' '}
                      <a href="mailto:support@ept.ai">support@ept.ai</a> with a
                      screenshot or the text below.
                    </p>
                    <p className="mb-1">
                      This tab has invalid data and cannot be displayed:
                    </p>
                    <ul className="mb-0">
                      {validation.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                </div>
              </Tab.Pane>
            );
          }

          // Handle valid tabs with invalid card_list or treeTabs
          const treeTabs = tab.treeTabs || tab.tree_tabs;

          if (tab.card_list && !Array.isArray(tab.card_list)) {
            return (
              <Tab.Pane key={tabId} eventKey={tabId}>
                <div className="tab-content-wrapper">
                  <Alert variant="warning">
                    <Alert.Heading>⚠️ Invalid Tab Content</Alert.Heading>
                    <p className="mb-2 text-muted small">
                      <strong>ept AI backend error.</strong> Please send an
                      email to{' '}
                      <a href="mailto:support@ept.ai">support@ept.ai</a> with a
                      screenshot or the text below.
                    </p>
                    <p className="mb-0">
                      This tab's card_list is not a valid array and cannot be
                      displayed.
                    </p>
                  </Alert>
                </div>
              </Tab.Pane>
            );
          }

          if (treeTabs && !Array.isArray(treeTabs)) {
            return (
              <Tab.Pane key={tabId} eventKey={tabId}>
                <div className="tab-content-wrapper">
                  <Alert variant="warning">
                    <Alert.Heading>⚠️ Invalid Tab Content</Alert.Heading>
                    <p className="mb-2 text-muted small">
                      <strong>ept AI backend error.</strong> Please send an
                      email to{' '}
                      <a href="mailto:support@ept.ai">support@ept.ai</a> with a
                      screenshot or the text below.
                    </p>
                    <p className="mb-0">
                      This tab's treeTabs is not a valid array and cannot be
                      displayed.
                    </p>
                  </Alert>
                </div>
              </Tab.Pane>
            );
          }

          // Handle valid tabs (including empty ones)
          return (
            <Tab.Pane key={tabId} eventKey={tabId}>
              <div className="tab-content-wrapper">
                {treeTabs && treeTabs.length > 0 ? (
                  // Render one or two TreeTabs with toggle when there are two
                  <Suspense
                    fallback={
                      <div className="text-muted p-3">
                        Loading tree navigation...
                      </div>
                    }
                  >
                    {treeTabs.length === 2 ? (
                      <TreeTabsSwitcher treeTabs={treeTabs} />
                    ) : (
                      treeTabs.map((treeTab, idx) => (
                        <ErrorBoundary
                          key={idx}
                          fallbackType="card"
                          title="TreeTab Error"
                        >
                          <TreeTabRenderer treeTab={treeTab} />
                        </ErrorBoundary>
                      ))
                    )}
                  </Suspense>
                ) : tab.card_list && tab.card_list.length > 0 ? (
                  // Render Cards
                  (() => {
                    const layoutConfig = tab.cards_layout ||
                      schema?.cards_layout || { layout: 'column', columns: 1 };
                    const isRow = layoutConfig.layout === 'row';
                    const columns = layoutConfig.columns || 1;
                    return (
                      <div
                        className={`cards-container ${layoutConfig.layout || 'column'}`}
                      >
                        <div
                          className={`cards-grid ${isRow ? 'row-layout' : 'column-layout'}`}
                          style={{
                            gridTemplateColumns: isRow
                              ? `repeat(${columns}, 1fr)`
                              : '1fr',
                          }}
                        >
                          {tab.card_list.map((card, idx) => (
                            <ErrorBoundary
                              key={idx}
                              fallbackType="card"
                              title="Card Error"
                            >
                              <CardRenderer card={card} />
                            </ErrorBoundary>
                          ))}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-5 text-muted">
                    <p className="mb-0">This tab is empty</p>
                    <small>No content has been configured for this tab.</small>
                  </div>
                )}
              </div>
            </Tab.Pane>
          );
        })}
      </Tab.Content>
    </Tab.Container>
  );
};

export default TabRenderer;
